import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CogIcon,
  UserIcon,
  BellIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { settingsAPI } from '../../services/api';
import Layout from '../../components/layout/Layout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

const AdminSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'system' | 'user' | 'notifications' | 'security' | 'analytics'>('system');
  const [systemSettings, setSystemSettings] = useState<any>(null);
  const [userSettings, setUserSettings] = useState<any>(null);
  const [, setNotificationSettings] = useState<any>(null);
  const [analyticsSummary, setAnalyticsSummary] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Mock current user ID - in a real app, this would come from auth context
  const currentUserId = 'current-user-id';

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const [system, user, notifications, analytics] = await Promise.all([
        settingsAPI.getSystemSettings(),
        settingsAPI.getUserSettings(currentUserId),
        settingsAPI.getNotificationSettings(currentUserId),
        settingsAPI.getAnalyticsSummary(),
      ]);
      setSystemSettings(system);
      setUserSettings(user);
      setNotificationSettings(notifications);
      setAnalyticsSummary(analytics);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSystemSettingsUpdate = async (updatedSettings: any) => {
    try {
      setIsSaving(true);
      const result = await settingsAPI.updateSystemSettings(updatedSettings);
      setSystemSettings(result);
      toast.success('System settings updated successfully');
    } catch (error) {
      toast.error('Failed to update system settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUserSettingsUpdate = async (updatedSettings: any) => {
    try {
      setIsSaving(true);
      const result = await settingsAPI.updateUserSettings(currentUserId, updatedSettings);
      setUserSettings(result);
      toast.success('User settings updated successfully');
    } catch (error) {
      toast.error('Failed to update user settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleNotificationSettingsUpdate = async (updatedSettings: any) => {
    try {
      setIsSaving(true);
      const result = await settingsAPI.updateNotificationSettings(currentUserId, updatedSettings);
      setNotificationSettings(result);
      toast.success('Notification settings updated successfully');
    } catch (error) {
      toast.error('Failed to update notification settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportData = async () => {
    try {
      setIsSaving(true);
      const data = await settingsAPI.exportData();
      
      // Create and download file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wm-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = [
    { id: 'system', name: 'System', icon: CogIcon },
    { id: 'user', name: 'User Preferences', icon: UserIcon },
    { id: 'notifications', name: 'Notifications', icon: BellIcon },
    { id: 'security', name: 'Security', icon: ShieldCheckIcon },
    { id: 'analytics', name: 'Analytics', icon: ChartBarIcon },
  ];

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <CogIcon className="w-8 h-8 text-primary-600" />
            <span>Admin Settings</span>
          </h1>
          <p className="text-gray-600">Manage system settings, user preferences, and organizational configurations</p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {activeTab === 'system' && systemSettings && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">System Configuration</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Application Name
                    </label>
                    <input
                      type="text"
                      value={systemSettings.appName}
                      onChange={(e) => setSystemSettings({ ...systemSettings, appName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Users Per Team
                    </label>
                    <input
                      type="number"
                      value={systemSettings.maxUsersPerTeam}
                      onChange={(e) => setSystemSettings({ ...systemSettings, maxUsersPerTeam: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Session Timeout (hours)
                    </label>
                    <input
                      type="number"
                      value={systemSettings.sessionTimeout}
                      onChange={(e) => setSystemSettings({ ...systemSettings, sessionTimeout: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Default Task Priority
                    </label>
                    <select
                      value={systemSettings.defaultTaskPriority}
                      onChange={(e) => setSystemSettings({ ...systemSettings, defaultTaskPriority: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="LOW">Low</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                    </select>
                  </div>
                </div>
                
                <div className="mt-6 space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="allowUserRegistration"
                      checked={systemSettings.allowUserRegistration}
                      onChange={(e) => setSystemSettings({ ...systemSettings, allowUserRegistration: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor="allowUserRegistration" className="ml-2 text-sm text-gray-700">
                      Allow user registration
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="requireEmailVerification"
                      checked={systemSettings.requireEmailVerification}
                      onChange={(e) => setSystemSettings({ ...systemSettings, requireEmailVerification: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor="requireEmailVerification" className="ml-2 text-sm text-gray-700">
                      Require email verification
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="maintenanceMode"
                      checked={systemSettings.maintenanceMode}
                      onChange={(e) => setSystemSettings({ ...systemSettings, maintenanceMode: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor="maintenanceMode" className="ml-2 text-sm text-gray-700">
                      Maintenance mode
                    </label>
                  </div>
                </div>

                <div className="mt-6">
                  <button
                    onClick={() => handleSystemSettingsUpdate(systemSettings)}
                    disabled={isSaving}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? 'Saving...' : 'Save System Settings'}
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'user' && userSettings && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">User Preferences</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Theme
                    </label>
                    <select
                      value={userSettings.theme}
                      onChange={(e) => setUserSettings({ ...userSettings, theme: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                      <option value="auto">Auto</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Language
                    </label>
                    <select
                      value={userSettings.language}
                      onChange={(e) => setUserSettings({ ...userSettings, language: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Timezone
                    </label>
                    <select
                      value={userSettings.timezone}
                      onChange={(e) => setUserSettings({ ...userSettings, timezone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">Eastern Time</option>
                      <option value="America/Chicago">Central Time</option>
                      <option value="America/Denver">Mountain Time</option>
                      <option value="America/Los_Angeles">Pacific Time</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Items Per Page
                    </label>
                    <select
                      value={userSettings.itemsPerPage}
                      onChange={(e) => setUserSettings({ ...userSettings, itemsPerPage: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>

                <div className="mt-6">
                  <button
                    onClick={() => handleUserSettingsUpdate(userSettings)}
                    disabled={isSaving}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? 'Saving...' : 'Save User Preferences'}
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'security' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Data Management</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">Export Data</h4>
                      <p className="text-sm text-gray-600">Download a backup of all system data</p>
                    </div>
                    <button
                      onClick={handleExportData}
                      disabled={isSaving}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4" />
                      <span>{isSaving ? 'Exporting...' : 'Export'}</span>
                    </button>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div>
                      <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                        <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />
                        <span>Import Data</span>
                      </h4>
                      <p className="text-sm text-gray-600">Import data from a backup file (use with caution)</p>
                    </div>
                    <button
                      disabled
                      className="flex items-center space-x-2 px-4 py-2 bg-gray-400 text-white rounded-md cursor-not-allowed"
                    >
                      <ArrowUpTrayIcon className="w-4 h-4" />
                      <span>Import</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'analytics' && analyticsSummary && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-600">Total Users</h4>
                  <p className="text-2xl font-bold text-gray-900">{analyticsSummary.overview.totalUsers}</p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-600">Total Teams</h4>
                  <p className="text-2xl font-bold text-gray-900">{analyticsSummary.overview.totalTeams}</p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-600">Total Tasks</h4>
                  <p className="text-2xl font-bold text-gray-900">{analyticsSummary.overview.totalTasks}</p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-600">Completion Rate</h4>
                  <p className="text-2xl font-bold text-gray-900">{analyticsSummary.overview.completionRate}%</p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">System Health</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-600">Uptime</h4>
                    <p className="text-lg font-semibold text-green-600">{analyticsSummary.systemHealth.uptime}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-600">Response Time</h4>
                    <p className="text-lg font-semibold text-blue-600">{analyticsSummary.systemHealth.responseTime}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-600">Error Rate</h4>
                    <p className="text-lg font-semibold text-yellow-600">{analyticsSummary.systemHealth.errorRate}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminSettings;
